import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Check, X } from "lucide-react";
import { toast } from "sonner";

/** AI-proposed new areas — approve (optionally renamed) or reject. */
export function AreaProposals({ proposals, disabled, onChanged }) {
    const [busy, setBusy] = useState(null);
    if (!proposals?.length) return null;
    const decide = async (p, approve) => {
        let name = p.name;
        if (approve) {
            name = window.prompt(`Create area "${p.name}"? Edit the name or confirm:`, p.name);
            if (name == null || !name.trim()) return;
        } else if (!window.confirm(`Reject "${p.name}"? Its ${p.label_count} label(s) stay under review.`)) return;
        setBusy(p.proposal_id);
        try {
            const r = await api.post(`/v1/ripeness/areas/proposals/${p.proposal_id}/${approve ? "approve" : "reject"}`, approve ? { name: name.trim() } : {});
            toast.success(approve ? `Area "${r.data.name}" created · ${r.data.tracks_reprojected ?? 0} tracks moved` : `Proposal "${p.name}" rejected`);
            onChanged?.();
        } catch (e) {
            toast.error(`Decision failed: ${formatApiErrorDetail(e)}`);
        } finally {
            setBusy(null);
        }
    };
    return (
        <div className="border border-violet-400/30 bg-violet-500/5 rounded p-2 space-y-1.5" data-testid="area-proposals">
            <div className="text-[11px] uppercase tracking-wider text-violet-200">AI proposes {proposals.length} new area{proposals.length > 1 ? "s" : ""} — your approval needed</div>
            {proposals.map((p) => (
                <div key={p.proposal_id} className="border border-slate-800 rounded p-2" data-testid={`area-proposal-${p.proposal_id}`}>
                    <div className="flex items-center gap-2">
                        <span className="text-slate-100 font-medium" data-testid={`area-proposal-name-${p.proposal_id}`}>{p.name}</span>
                        <span className="text-slate-500">{p.label_count} label{p.label_count > 1 ? "s" : ""}</span>
                        <div className="ml-auto flex gap-1">
                            <button onClick={() => decide(p, true)} disabled={disabled || busy === p.proposal_id}
                                className="inline-flex items-center gap-1 rounded border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-40"
                                data-testid={`area-proposal-approve-${p.proposal_id}`}><Check className="w-3 h-3" /> Approve</button>
                            <button onClick={() => decide(p, false)} disabled={disabled || busy === p.proposal_id}
                                className="inline-flex items-center gap-1 rounded border border-rose-500/40 bg-rose-500/10 px-2 py-0.5 text-rose-200 hover:bg-rose-500/20 disabled:opacity-40"
                                data-testid={`area-proposal-reject-${p.proposal_id}`}><X className="w-3 h-3" /> Reject</button>
                        </div>
                    </div>
                    {p.rationale ? <div className="text-[11px] text-slate-500 mt-0.5">{p.rationale}</div> : null}
                    <div className="text-[11px] text-slate-400 mt-0.5 truncate" title={(p.labels || []).join(", ")} data-testid={`area-proposal-labels-${p.proposal_id}`}>{(p.labels || []).join(" · ")}</div>
                </div>
            ))}
        </div>
    );
}
